/* eslint-disable sonarjs/cognitive-complexity */
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import Head from 'next/head';
import { useTranslation } from 'next-i18next';
import { Question, Quotation, Thing, VideoObject, WithContext } from 'schema-dts';

import { RichText } from '@boss/rich-text';
import type { Component, IIVacancy, NewsArticleFields, ThemeColor } from '@boss/types/corporate-jobs';
import {
  Accordion,
  BrandCards,
  Button,
  CardCta,
  Image as CfImage,
  CorporateValueCard,
  Fallback,
  Hero,
  JobsPromotionBanner,
  NumberCard,
  PhotoCollection,
  QuickLinks,
  Quote,
  Section,
  Video,
} from '@boss/ui';

import { ContactForm, ContactInformation, HighlightedJobs, HighlightedNews, InstagramFeed } from '../../components';
import {
  getBrandCardProps,
  getButtonProps,
  getCardCtaProps,
  getFaqProps,
  getHeroProps,
  getImageProps,
  getNumberCardProps,
  getPhotoCollectionProps,
  getQuickLinkProps,
  getQuoteProps,
  getRichTextProps,
  getSectionProps,
  getValueCardProps,
  getVideoProps,
} from '../PropsMapping';
import getContentPageCardProps from '../PropsMapping/contentPage';
import getPromotionCtaProps from '../PropsMapping/promotionCta';

type Props = {
  additionalProps?: Record<string, unknown>;
  embeddedEntry?: boolean;
  entry: Component;
  environment?: string;
  background?: boolean;
  highlightedJobs?: IIVacancy[] | null;
  mostRecentJobs?: IIVacancy[] | null;
  highlightedNews?: NewsArticleFields[] | null;
  inline?: boolean;
  locale: string;
  themeColor?: ThemeColor;
  alignment?: 'left' | 'center';
};

const schemaString = 'https://schema.org';
/**
 * Dynamicly allocate schema.org data to the head
 */
const renderSchemaOrg = <T extends Thing>(schemaData: WithContext<T>) => (
  <Head>
    <script dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} type="application/ld+json" />
  </Head>
);

export const Mapper = ({
  additionalProps,
  background,
  entry,
  environment = 'test',
  highlightedJobs,
  highlightedNews,
  inline,
  locale,
  mostRecentJobs,
  themeColor,
}: Props): JSX.Element | null => {
  const { t } = useTranslation(['common', 'jobs']);
  const alignment = additionalProps?.alignment;
  const hasBackground = themeColor ? background ?? !!additionalProps?.background ?? undefined : false;

  const type = entry?.__typename;
  const parentIsSection = additionalProps?.parent === 'Section';
  const parentHasColumns = additionalProps?.displayAs !== '1-column';
  const isInColored2ColumnsSection: boolean | undefined =
    !!additionalProps?.parentIsColored2ColumnsSection ||
    (additionalProps?.parent === 'section' && additionalProps?.displayAs === '2-columns-colored') ||
    (hasBackground && !!themeColor);
  const textClassName = hasBackground && !!themeColor ? 'text-white' : undefined;

  if (type === 'brand') {
    return <BrandCards {...getBrandCardProps(entry)} />;
  }

  if (type === 'cardCta') {
    return <CardCta {...getCardCtaProps(entry, locale, hasBackground)} />;
  }

  if (type === 'collection') {
    return <PhotoCollection {...getPhotoCollectionProps(entry)} />;
  }

  if (type === 'contactInformation') {
    return <ContactInformation entry={entry} hasBackground={hasBackground} locale={locale} />;
  }

  if (type === 'contentPage') {
    return (
      <CardCta
        {...getContentPageCardProps(entry, locale, themeColor)}
        cta={{
          href: 'slug' in entry ? entry.slug : '',
          id: entry.id,
          label: t('common:moreInfoLabel'),
        }}
      />
    );
  }

  if (type === 'cta') {
    if (!entry.internalLink && !entry.externalLink && !entry.fileToDownload) {
      return <Fallback componentType={type} env={environment} type="required-content" />;
    }

    if (alignment === 'center' && !inline) {
      return (
        <span className="flex w-full flex-row items-center justify-center">
          <Button
            {...getButtonProps(entry, locale, isInColored2ColumnsSection, inline, hasBackground)}
            {...additionalProps}
          />
        </span>
      );
    }

    return (
      <Button
        {...getButtonProps(entry, locale, isInColored2ColumnsSection, inline, hasBackground)}
        {...additionalProps}
      />
    );
  }

  if (type === 'faq') {
    return (
      <>
        {renderSchemaOrg<Question>({
          '@context': schemaString,
          '@type': 'Question',
          text: documentToPlainTextString(entry.question),
          acceptedAnswer: {
            '@type': 'Answer',
            text: documentToPlainTextString(entry.answer.content),
          },
        })}
        <Accordion
          aria-label="faq"
          variant="primary"
          {...getFaqProps(entry, locale, themeColor, hasBackground)}
          {...additionalProps}
        />
      </>
    );
  }

  if (type === 'hero') {
    return (
      <>
        {entry.backgroundVideo
          ? renderSchemaOrg<VideoObject>({
              '@context': schemaString,
              '@type': 'VideoObject',
              name: entry.backgroundVideo?.title,
              '@id': entry.backgroundVideo?.file?.url,
            })
          : null}

        <Hero {...getHeroProps(entry, locale)} />
      </>
    );
  }

  if (type === 'image') {
    const className = parentIsSection ? 'max-h-1/2-screen min-h-full' : '';

    return <CfImage {...getImageProps(entry)} {...additionalProps} className={className} />;
  }

  if (type === 'numberCard') {
    return <NumberCard {...getNumberCardProps(entry, locale, themeColor, hasBackground)} />;
  }

  if (type === 'promotionCta') {
    return <JobsPromotionBanner {...getPromotionCtaProps(entry, locale)} isInSplitSection={parentHasColumns} />;
  }

  if (type === 'quickLinks') {
    return <QuickLinks {...getQuickLinkProps(entry, locale, hasBackground)} />;
  }

  if (type === 'quote') {
    return (
      <>
        {renderSchemaOrg<Quotation>({
          '@context': schemaString,
          '@type': 'Quotation',
          text: entry.quote,
          spokenByCharacter: {
            '@type': 'Person',
            name: entry.author,
          },
        })}
        <Quote {...getQuoteProps(entry, themeColor, hasBackground)} variant="secondary" />
      </>
    );
  }

  if (type === 'richText') {
    return (
      <RichText {...getRichTextProps(entry, locale, themeColor, isInColored2ColumnsSection)} {...additionalProps} />
    );
  }

  if (type === 'section') {
    return (
      <Section
        {...getSectionProps(entry, locale, environment, highlightedJobs, mostRecentJobs, highlightedNews, themeColor)}
        titleClassName="max-w-full"
      />
    );
  }

  // tab items outside a tabs section are displayed as value cards
  if (type === 'tabItem') {
    return <CorporateValueCard {...getValueCardProps(entry, locale, hasBackground)} />;
  }

  if (type === 'video') {
    return <Video {...getVideoProps(entry)} />;
  }

  if (type === 'widget' && entry.widgetType === 'corporate-contact-form') {
    return <ContactForm textClassName={textClassName} />;
  }

  if (type === 'widget' && entry.widgetType === 'instagram-section') {
    return <InstagramFeed locale={locale} />;
  }

  if (type === 'widget' && entry.widgetType === 'highlighted-jobs') {
    if (highlightedJobs && highlightedJobs.length > 0) {
      return <HighlightedJobs highlightedJobs={highlightedJobs} locale={locale} />;
    } else if (mostRecentJobs && mostRecentJobs.length > 0) {
      return <HighlightedJobs highlightedJobs={mostRecentJobs} locale={locale} />;
    } else {
      return <p className={textClassName}>{t('jobs:noResult')}</p>;
    }
  }

  if (type === 'widget' && entry.widgetType === 'highlighted-news') {
    if (highlightedNews && highlightedNews.length !== 0) {
      return <HighlightedNews highlightedNews={highlightedNews} locale={locale} textClassName={textClassName} />;
    } else {
      return <Fallback componentType={type} env={environment} type="required-content" />;
    }
  }

  // Following components should be omitted when used in regular sections
  if (type === 'timelineEvent') {
    return <Fallback componentType={type} env={environment} type="excluded-section" />;
  }

  return <Fallback componentType={type} env={environment} type="not-implemented" />;
};

const ComponentMapper = ({
  additionalProps,
  entry,
  environment,
  background,
  highlightedJobs,
  highlightedNews,
  inline,
  locale,
  mostRecentJobs,
  themeColor,
}: Props) => {
  const body = (
    <Mapper
      additionalProps={additionalProps}
      background={background}
      entry={entry}
      environment={environment}
      highlightedJobs={highlightedJobs}
      highlightedNews={highlightedNews}
      inline={inline}
      locale={locale}
      mostRecentJobs={mostRecentJobs}
      themeColor={themeColor}
    />
  );

  return inline ? <span className="inline px-2">{body}</span> : body;
};

export default ComponentMapper;
